import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { POLL_INTERVAL_MS, MAX_POLL_ATTEMPTS } from '../config/playback';

const GenerationContext = createContext(null);

/**
 * Tracks book generation status across route changes.
 * Polls the backend for real progress instead of simulating it.
 *
 * Each generating book is tracked by ID with its current status.
 */
export function GenerationProvider({ children }) {
  // { [bookId]: { status: 'processing'|'complete'|'error', chunksReady, totalChunkCount, error } }
  const [generations, setGenerations] = useState({});
  const pollIntervalsRef = useRef({});

  // Clean up all polls on unmount
  useEffect(() => {
    const pollIntervals = pollIntervalsRef.current;
    return () => {
      Object.values(pollIntervals).forEach(clearInterval);
    };
  }, []);

  const pollCountsRef = useRef({});

  const pollBookStatus = useCallback((bookId) => {
    // Don't start duplicate polls
    if (pollIntervalsRef.current[bookId]) return;

    pollCountsRef.current[bookId] = 0;

    const poll = setInterval(async () => {
      pollCountsRef.current[bookId]++;
      if (pollCountsRef.current[bookId] >= MAX_POLL_ATTEMPTS) {
        console.warn(`[GenerationContext] Polling stopped after ${MAX_POLL_ATTEMPTS} attempts for book ${bookId}`);
        clearInterval(pollIntervalsRef.current[bookId]);
        delete pollIntervalsRef.current[bookId];
        delete pollCountsRef.current[bookId];
        setGenerations(prev => ({
          ...prev,
          [bookId]: { status: 'error', error: 'Generation timed out. Please refresh to check status.' }
        }));
        return;
      }
      try {
        const res = await fetch(apiUrl(`/api/books/${bookId}`));
        if (!res.ok) return;

        const book = await res.json();

        if (book.status === 'complete') {
          setGenerations(prev => ({
            ...prev,
            [bookId]: { status: 'complete' }
          }));
          clearInterval(pollIntervalsRef.current[bookId]);
          delete pollIntervalsRef.current[bookId];
        } else if (book.status === 'error') {
          setGenerations(prev => ({
            ...prev,
            [bookId]: { status: 'error', error: 'Generation failed' }
          }));
          clearInterval(pollIntervalsRef.current[bookId]);
          delete pollIntervalsRef.current[bookId];
        }
        // If still processing, also try manifest for chunk progress
        else if (book.status === 'processing') {
          try {
            const manifestRes = await fetch(apiUrl(`/api/books/${bookId}/manifest`));
            if (manifestRes.ok) {
              const manifest = await manifestRes.json();
              setGenerations(prev => ({
                ...prev,
                [bookId]: {
                  status: 'processing',
                  chunksReady: manifest.chunksReady || manifest.chunkCount || 0,
                  totalChunkCount: manifest.totalChunkCount || null
                }
              }));
            }
          } catch {
            // Manifest not ready yet, that's fine
          }
        }
      } catch (err) {
        console.error(`[GenerationContext] Poll error for book ${bookId}:`, err);
      }
    }, POLL_INTERVAL_MS);

    pollIntervalsRef.current[bookId] = poll;
  }, []);

  const startGeneration = useCallback((bookId) => {
    setGenerations(prev => ({
      ...prev,
      [bookId]: { status: 'processing', chunksReady: 0, totalChunkCount: null }
    }));
    pollBookStatus(bookId);
  }, [pollBookStatus]);

  const clearGeneration = useCallback((bookId) => {
    setGenerations(prev => {
      const next = { ...prev };
      delete next[bookId];
      return next;
    });
    if (pollIntervalsRef.current[bookId]) {
      clearInterval(pollIntervalsRef.current[bookId]);
      delete pollIntervalsRef.current[bookId];
    }
  }, []);

  const getGeneration = useCallback((bookId) => {
    return generations[bookId] || null;
  }, [generations]);

  return (
    <GenerationContext.Provider value={{ generations, startGeneration, clearGeneration, getGeneration }}>
      {children}
    </GenerationContext.Provider>
  );
}

export function useGeneration() {
  const ctx = useContext(GenerationContext);
  if (!ctx) {
    throw new Error('useGeneration must be used within a GenerationProvider');
  }
  return ctx;
}
